/////////////////////////////////////////////////////
//     							                   //
//  Program: CheckinHouseRules.jsx                 //
//  Application: Property                          //
//  Option: itemized, per-property check-in        //
//          house rules                             //
//  Date: 2026-07-09                               //
//                                                 //
/////////////////////////////////////////////////////

import React, { Component } from "react";
import editIcon from "./../../assets/images/edit-pen-icon.svg";
import { Button, Table, Form, InputGroup, Container } from "react-bootstrap";
import SimpleReactValidator from "simple-react-validator";
import CheckinHouseRuleService from "../../services/CheckinHouseRuleService";
import Alerts from "../common/Alerts";
import PropertySidebar from "../common/PropertySidebar";

// Standalone page, deliberately not part of HouseRules.jsx - own route, own nav
// entry, own Edit/Save/Cancel flow. See ADR-19 in the TDD.
export class CheckinHouseRules extends Component {
  constructor(props) {
    super(props);

    this.validator = new SimpleReactValidator({ autoForceUpdate: this });

    this.state = {
      property_slug: this.props.match.params.propertySlug,
      checkinHouseRules: [],
      originalCheckinHouseRules: [],
      viewMode: 0,
      confirm_back: 0,
      showSpinner: false,
      loading: false,
      isSubmit: false,
      showAlertModal: false,
      alertModalType: "",
      alertModalTitle: "",
      alertModalMessage: "",
    };

    this.changeView = this.changeView.bind(this);
    this.handleAddRule = this.handleAddRule.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidMount() {
    this.getCheckinHouseRules();
  }

  async getCheckinHouseRules() {
    this.setState({ showSpinner: true, loading: true });
    var checkinHouseRules = [];
    let res = await CheckinHouseRuleService.getCheckinHouseRules(
      this.state.property_slug
    );
    if (global.successStatus.includes(res.status)) {
      let list = res.data ? res.data : [];
      checkinHouseRules = list.map((item) => ({
        slug: item.slug,
        rule_text: item.rule_text,
        checkbox_text: item.checkbox_text,
        is_required: item.is_required === 1,
        sort_order: item.sort_order,
        status_id: item.status_id === 1,
      }));
    }
    this.setState({
      checkinHouseRules: checkinHouseRules,
      originalCheckinHouseRules: JSON.parse(
        JSON.stringify(checkinHouseRules)
      ),
      showSpinner: false,
      loading: false,
    });
  }

  changeView() {
    if (this.state.viewMode === 1) {
      // Cancel - discard in-progress edits, including any unsaved added/removed rows
      this.setState({
        viewMode: 0,
        confirm_back: 0,
        checkinHouseRules: JSON.parse(
          JSON.stringify(this.state.originalCheckinHouseRules)
        ),
      });
      this.validator.hideMessages();
    } else {
      this.setState({ viewMode: 1, confirm_back: 0 });
    }
  }

  handleRowFieldChange = (index, field, value) => {
    let rows = [...this.state.checkinHouseRules];
    rows[index] = { ...rows[index], [field]: value };
    this.setState({ checkinHouseRules: rows, confirm_back: 1 });
  };

  handleToggle = (index, field) => {
    let rows = [...this.state.checkinHouseRules];
    rows[index] = { ...rows[index], [field]: !rows[index][field] };
    this.setState({ checkinHouseRules: rows, confirm_back: 1 });
  };

  getNextSortOrder() {
    let max = 0;
    this.state.checkinHouseRules.forEach((row) => {
      let value = parseInt(row.sort_order, 10);
      if (!isNaN(value) && value > max) {
        max = value;
      }
    });
    return max + 1;
  }

  handleAddRule() {
    let rows = [...this.state.checkinHouseRules];
    rows.push({
      rule_text: "",
      checkbox_text: "Yes, I agree",
      is_required: true,
      sort_order: this.getNextSortOrder(),
      status_id: true,
    });
    this.setState({ checkinHouseRules: rows, confirm_back: 1 });
  }

  handleRemoveUnsavedRow = (index) => {
    let rows = [...this.state.checkinHouseRules];
    if (rows[index] && !rows[index].slug) {
      rows.splice(index, 1);
      this.setState({ checkinHouseRules: rows, confirm_back: 1 });
      // Row indices shift after a splice, and SimpleReactValidator tracks
      // messages by field name (rule_text_<index>) - clear stale messages
      // so they don't attach to the wrong row on the next render.
      this.validator.hideMessages();
    }
  };

  customValidate() {
    return true;
  }

  handleSave = async (e) => {
    e.preventDefault();
    if (this.validator.allValid() && this.customValidate()) {
      this.setState({ isSubmit: true });
      let rules = this.state.checkinHouseRules.map((row) => {
        let payloadRow = {
          rule_text: row.rule_text,
          checkbox_text: row.checkbox_text,
          is_required: row.is_required ? 1 : 0,
          sort_order: row.sort_order,
          status_id: row.status_id ? 1 : 2,
        };
        if (row.slug) {
          payloadRow.slug = row.slug;
        }
        return payloadRow;
      });

      let inputData = {
        property_slug: this.state.property_slug,
        rules: rules,
      };

      let res = await CheckinHouseRuleService.addOrUpdateCheckinHouseRules(
        inputData
      );
      if (global.successStatus.includes(res.status)) {
        this.setState({
          showAlertModal: true,
          alertModalType: "success",
          alertModalTitle: "Success",
          alertModalMessage: res.message ? res.message : "Success",
          viewMode: 0,
          confirm_back: 0,
        });
        this.getCheckinHouseRules();
      } else {
        // add-update-property-house-rule has no per-field errorDetail (verified
        // against PropertyHouseRule::addUpdateHouseRules, which returns a plain
        // validation message string) - show it as a single generic alert.
        this.setState({
          showAlertModal: true,
          alertModalType: "error",
          alertModalTitle: "Error!",
          alertModalMessage:
            res.data && res.data.message
              ? res.data.message
              : res.message
              ? res.message
              : "Error!",
        });
      }
      this.setState({ isSubmit: false });
      setTimeout(
        () => this.setState({ showAlertModal: false }),
        global.alert_close_time
      );
    } else {
      this.validator.showMessages();
    }
  };

  getTitle() {
    return "Check-in House Rules";
  }

  render() {
    const { checkinHouseRules, viewMode } = this.state;
    return (
      <main>
        <Container className="innter-container">
          <div className="d-flex flex-column">
            <div className="row row-xs mg-t-0 pd-t-0 pd-lg-l-15 pd-xs-l-0">
              <div className="col-md-2 left">
                <div className="child-menu-section">
                  <PropertySidebar property_slug={this.state.property_slug} />
                </div>
              </div>

              <div className="col-md-10 right">
                <div className="col-md-12 mg-t-20 mg-b-10"></div>
                <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-l-15 pd-r-0">
                  <div className="dashbaord-pageHeadertitle d-flex justify-content-between align-items-center flex-wrap mg-b-0">
                    <h3>{this.getTitle()}</h3>
                    <div className="d-flex justify-content-end pd-l-0 pd-r-0 mg-b-0">
                      {viewMode === 0 ? (
                        <>
                          {global.userPermissions.checkPermission(
                            "house-rules-update"
                          ) && (
                            <button
                              className="btn-success-outline-small"
                              onClick={this.changeView}
                            >
                              <img src={editIcon} alt="" />
                              Edit
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="d-flex">
                          <button
                            type="button"
                            className="btn-success-outline-small"
                            onClick={() => {this.changeView();}}
                          >
                            Cancel
                          </button>
                          {global.userPermissions.checkPermission(
                            "house-rules-update"
                          ) && (
                            <button
                              type="button"
                              className="btn-success ml-3"
                              onClick={this.handleSave}
                              disabled={this.state.isSubmit ? true : false}
                            >
                              {this.state.isSubmit ? global.loader : "Save"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="scrolling-carousel pd-xs-l-0 pd-xs-r-0 pd-lg-l-30 pd-lg-r-30">
                  <div className="dashboardRightcard dashbaord-pageHeadertitle- background-none pt-0 pd-l-30 pd-r-30 swipersectionBtns">
                    <div className="col-md-12 pd-l-15 pd-r-0 mg-t-0">
                      <Alerts
                        show={this.state.showAlertModal}
                        type={this.state.alertModalType}
                        title={this.state.alertModalTitle}
                        message={this.state.alertModalMessage}
                      />
                    </div>

                    {viewMode === 0 ? (
                      <div className="row mg-b-15">
                        <div className="col-md-12 pd-l-30 pd-r-30 pd-t-25">
                          {checkinHouseRules.length > 0 ? (
                            <Table hover className="mg-b-0">
                              <thead>
                                <tr>
                                  <th width="40%">Check-in House Rules</th>
                                  <th width="5%">&nbsp;</th>
                                  <th width="15%">Checkbox Label</th>
                                  <th width="10%">Required</th>
                                  <th width="10%">Sort Order</th>
                                  <th width="5%">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {checkinHouseRules.map((row, i) => (
                                  <tr key={row.slug ? row.slug : "row-" + i}>
                                    <td>{row.rule_text}</td>
                                    <td>&nbsp;</td>
                                    <td>{row.checkbox_text}</td>
                                    <td>
                                      {row.is_required
                                        ? "Required"
                                        : "Optional"}
                                    </td>
                                    <td>{row.sort_order}</td>
                                    <td>
                                      {row.status_id ? (
                                        <b className="status-active"></b>
                                      ) : (
                                        <b className="status-inactive"></b>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <p className="para">
                              No check-in house rules configured yet.
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="row mg-b-15">
                        <div className="col-md-12 pd-l-30 pd-r-30 pd-t-25">
                          <Table className="mg-b-0">
                            <thead>
                              <tr>
                                  <th width="40%">Check-in House Rules</th>
                                  <th width="5%">&nbsp;</th>
                                  <th width="15%">Checkbox Label</th>
                                  <th width="10%">Required</th>
                                  <th width="10%">Sort Order</th>
                                  <th width="5%">Status</th>
                                  <th width="5%">&nbsp;</th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkinHouseRules.map((row, i) => (
                                <tr key={row.slug ? row.slug : "row-" + i}>
                                  <td>
                                    <Form.Control
                                      as="textarea"
                                      rows={2}
                                      name={"rule_text_" + i}
                                      value={row.rule_text}
                                      onChange={(e) =>
                                        this.handleRowFieldChange(
                                          i,
                                          "rule_text",
                                          e.target.value
                                        )
                                      }
                                      className="form-control max_width_100"
                                    />
                                    {this.validator.message(
                                      "rule_text_" + i,
                                      row.rule_text,
                                      "required",
                                      { className: "text-danger" }
                                    )}
                                  </td>
                                  <td>&nbsp;</td>
                                  <td>
                                    <Form.Control
                                      name={"checkbox_text_" + i}
                                      value={row.checkbox_text}
                                      placeholder="Yes, I agree"
                                      onChange={(e) =>
                                        this.handleRowFieldChange(
                                          i,
                                          "checkbox_text",
                                          e.target.value
                                        )
                                      }
                                      className="form-control max_width_100"
                                    />
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div
                                        className={
                                          row.is_required
                                            ? "az-toggle az-toggle-required-optional on ml-2"
                                            : "az-toggle az-toggle-required-optional ml-2"
                                        }
                                        onClick={() =>
                                          this.handleToggle(i, "is_required")
                                        }
                                      >
                                        <span></span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <InputGroup>
                                      <Form.Control
                                        name={"sort_order_" + i}
                                        value={row.sort_order}
                                        onChange={(e) =>
                                          this.handleRowFieldChange(
                                            i,
                                            "sort_order",
                                            e.target.value
                                          )
                                        }
                                        onKeyPress={
                                          global.onKeyPressEvent
                                            .numberValidation
                                        }
                                        className="form-control"
                                        type="text"
                                      />
                                    </InputGroup>
                                    {this.validator.message(
                                      "sort_order_" + i,
                                      row.sort_order,
                                      "required|numeric|min:0,num",
                                      { className: "text-danger" }
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div
                                        className={
                                          row.status_id
                                            ? "az-toggle on ml-2"
                                            : "az-toggle ml-2"
                                        }
                                        onClick={() =>
                                          this.handleToggle(i, "status_id")
                                        }
                                      >
                                        <span></span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    {!row.slug && (
                                      <span
                                        className="hand-cursor text-danger"
                                        onClick={() =>
                                          this.handleRemoveUnsavedRow(i)
                                        }
                                      >
                                        Remove
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          <Button
                            variant="outline-dark btn-block btn-with-icon mt-3"
                            onClick={this.handleAddRule}
                          >
                            Add Rule
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </main>
    );
  }
}

export default CheckinHouseRules;
